// =========================================================================
// Workflows — the section landing. Route `/workflows`.
// Demo `renderSectionPage('workflows')`, source lines 3334 and 3364.
//
// Three bands, in the demo's own order:
//
//   1. **Hero** — the section mark over the contour background, the lead
//      paragraph, and four figures: modules, open jobs, agents, systems.
//   2. **Ten module tiles** — numbered, in the module's own colour, each
//      carrying its open-job count and (this port's addition) its agent and
//      step count, which is what a reader choosing between ten of them needs.
//   3. **Lineage** — the fourteen capabilities of the original aftermarket
//      wheel, what each was, what it is now, and which modules absorbed it.
//
// The lineage strip is the one structural difference from the demo, which
// showed it per module, filtered. Both views ship: the module page keeps the
// filtered one. See `lineageCards()` for why this page carries all fourteen.
//
// UI layer. Fork-owned (zone C), P5a lane.
// =========================================================================

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';

import { SceneSection, SourceLink, TonePill } from '@/versions/v1/shared/oem-tiles';
import { MODULE_IDS, type ModuleId } from '@/versions/v1/shared/seed/modules';
import { renderStat } from './live-figure';
import { SectionHero } from './SectionHero';
import { SectionTile } from './SectionTile';
import { useFigureContext } from './use-figure-context';
import { useSeedNav } from './use-seed-nav';
import {
  MODULE_LABELS,
  WORKFLOWS_COLOUR,
  WORKFLOWS_ICON,
  WORKFLOWS_LEAD,
  WORKFLOWS_TAG,
  heroStats,
  lineageCards,
  moduleTiles,
} from './WorkflowsSectionScene.transform';

export function WorkflowsSectionScene() {
  const navigate = useNavigate();
  const { figures, ctx, loading, error } = useFigureContext();
  const { dispatch } = useSeedNav();

  const view = useMemo(() => {
    if (!ctx) return null;

    // `jobsByStage(stage, ctx)` is the engine's per-module count, but calling
    // it ten times rebuilds the whole job list ten times. The list is already
    // assembled on the context, so it is bucketed once — same numbers, one
    // pass — and `jobsByStage` stays the single definition of what a stage is.
    const counts: Record<string, number> = Object.fromEntries(
      MODULE_IDS.map((id: ModuleId) => [id, 0]),
    );
    for (const job of ctx.derived.jobs) {
      if (job.stage in counts) counts[job.stage] += 1;
    }

    return {
      tiles: moduleTiles(counts),
      stats: heroStats(ctx.derived.jobs.length),
      lineage: lineageCards(),
    };
  }, [ctx]);

  const openModule = useCallback(
    (id: ModuleId) => navigate(`/workflows/modules/${id}`),
    [navigate],
  );

  return (
    <SurfacePageLayout width="full">
      <Commentable name="WorkflowsSectionScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8 pt-6">
          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              The aftermarket data could not be loaded ({error.message}). The module list
              below is the operating model; the figures on it are not shown rather than
              shown wrong.
            </div>
          ) : null}

          <Commentable name="Hero">
            <SectionHero
              colour={WORKFLOWS_COLOUR}
              icon={WORKFLOWS_ICON}
              title="Workflows"
              tag={WORKFLOWS_TAG}
              lead={WORKFLOWS_LEAD}
              stats={view ? view.stats : []}
              actions={<HelpAnchor doc="workflows/section" />}
            />
          </Commentable>

          {/* fork(QA2): the landing is not a module — `module`'s prompts ask
              "what is waiting in this module?", which has no referent here. */}
          <AskStrip view="workflows" />

          {/* ---------------------------------------------------------- */}
          {/* The ten modules                                            */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="ModuleGrid">
            <SceneSection
              title="The ten modules"
              subtitle="every job on the board sits in exactly one of these"
              actions={
                <SourceLink onClick={() => navigate('/job-board')}>Job board</SourceLink>
              }
            >
              <div
                className="mb-2.5 flex justify-between text-[10px] uppercase tracking-[0.1em]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                <span>From assets and data</span>
                <span>To action</span>
                <span>To value and growth</span>
              </div>

              {view ? (
                <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3 @6xl:grid-cols-4">
                  {view.tiles.map((tile) => (
                    <SectionTile
                      key={tile.id}
                      colour={tile.colour}
                      icon={tile.icon}
                      label={tile.label}
                      description={tile.tag}
                      count={tile.count}
                      onSelect={() => openModule(tile.id)}
                      cta={`${tile.steps} step${tile.steps === 1 ? '' : 's'} · ${tile.agent} ›`}
                    />
                  ))}
                </div>
              ) : (
                <SkeletonCardGrid count={10} cols={3} />
              )}
            </SceneSection>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* Lineage                                                    */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Lineage">
            <SceneSection
              title="Lineage"
              subtitle="the fourteen capabilities of the original aftermarket wheel, and where each one lives now"
            >
              {view ? (
                <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3">
                  {view.lineage.map((cap) => {
                    const stat = figures ? renderStat(cap.stat, figures) : '';
                    return (
                      <article
                        key={cap.id}
                        className="flex min-w-0 flex-col gap-1.5 rounded-xl border p-3.5"
                        style={{
                          borderColor: 'var(--hairline)',
                          background: 'var(--surface-2)',
                          borderLeft: `3px solid ${
                            cap.coverageKey === 'full' ? 'var(--band-good)' : 'var(--accent)'
                          }`,
                        }}
                      >
                        <h3 className="flex items-center gap-2 text-[13px] font-semibold">
                          <span
                            className="mono text-[11px] font-medium"
                            style={{ color: 'var(--ink-tertiary)' }}
                          >
                            {cap.n}
                          </span>
                          <span className="min-w-0" style={{ color: 'var(--ink-primary)' }}>
                            {cap.name}
                          </span>
                          <TonePill
                            className="ml-auto shrink-0"
                            tone={cap.coverageKey === 'full' ? 'good' : 'flat'}
                          >
                            {cap.coverage}
                          </TonePill>
                        </h3>

                        <p
                          className="text-[11.5px] leading-[1.45]"
                          style={{ color: 'var(--ink-tertiary)' }}
                        >
                          <b className="font-semibold">Was:</b> {cap.then}
                        </p>
                        <p
                          className="text-[12.5px] leading-[1.5]"
                          style={{ color: 'var(--ink-secondary)' }}
                        >
                          {cap.now}
                        </p>

                        <p className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                          <b className="font-semibold">Agent:</b> {cap.agent}
                          {stat ? (
                            <>
                              {' · '}
                              <b className="font-semibold">Live:</b> {stat}
                            </>
                          ) : null}
                        </p>

                        {cap.modules.length > 0 ? (
                          <p className="flex flex-wrap gap-1.5 pt-0.5">
                            {cap.modules.map((id) => (
                              <button
                                key={id}
                                type="button"
                                onClick={() => openModule(id)}
                                className="rounded-md border px-1.5 py-px text-[10.5px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                                style={{
                                  borderColor: 'var(--hairline-strong)',
                                  color: 'var(--ink-secondary)',
                                }}
                              >
                                {MODULE_LABELS[id] ?? id}
                              </button>
                            ))}
                          </p>
                        ) : null}

                        {cap.links.length > 0 ? (
                          <p className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5">
                            {cap.links.map((link) =>
                              link.action ? (
                                <SourceLink
                                  key={link.label}
                                  onClick={() =>
                                    dispatch(
                                      link.openDoc
                                        ? { kind: 'openDoc', key: link.openDoc }
                                        : { kind: 'show', action: link.action! },
                                    )
                                  }
                                >
                                  {link.label}
                                </SourceLink>
                              ) : (
                                <span
                                  key={link.label}
                                  className="text-[11.5px]"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {link.label}
                                </span>
                              ),
                            )}
                          </p>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              ) : (
                <SkeletonBlock className="h-64 rounded-xl" />
              )}
            </SceneSection>
          </Commentable>

          {loading && !view ? (
            <p className="sr-only" role="status">
              Loading the aftermarket data.
            </p>
          ) : null}

          <p className="pt-2 text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
            Proof of concept · every figure on this page is the 09 Sep 2026 snapshot of the
            demonstration data pack, derived at render time rather than typed in.
          </p>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

export default WorkflowsSectionScene;
